import React, { useState, useEffect } from "react";
import { X, Bot, TrendingUp, Calendar, Info, ArrowRight, ArrowLeft, Clock, MessageSquare } from "lucide-react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Link } from "react-router-dom";
import { toast } from "react-hot-toast";
import { useOrganization } from '../../../context/OrganizationContext';
import { useUser } from '../../../context/UserContext';
import { createThread } from '../../../services/threadService';

/*

const payload = {
  // Core fields
  prompt: "What's my top traffic sources?",
  agentType: "linear",
  granularity: "daily",
  
  // Clean period type
  periodType: "rolling", // or "fixed"
  
  // Always 1-2 date ranges
  dateRanges: [
    {
      id: "primary",
      startDate: "2024-12-01",
      endDate: "2024-12-31",
      calculated: true // vs manually entered
    },
    {
      id: "comparison", // only if comparison enabled
      startDate: "2023-12-01",
      endDate: "2023-12-31",
      calculated: true // auto-calculated for sameLastYear
    }
  ],
  
  // Configuration object
  config: {
    // Rolling config (only if periodType === "rolling")
    rolling: {
      days: 30 // 7, 30, or 90
    },
    
    // Comparison config
    comparison: {
      enabled: true,
      type: "sameLastYear", // or "custom"
      autoCalculated: true // false for custom dates
    },
    
    // Automation metadata
    automation: {
      eligible: true // true only if periodType === "rolling"
    }
  }
}

*/


// Validation schema
const schema = yup.object({
  content: yup
    .string()
    .required("Content is required")
    .min(5, "Content must be at least 5 characters")
    .max(3200, "Content must not exceed 3200 characters"),
  agentType: yup.string().required("Agent type is required"),
  dateRange: yup.string().required("Date range is required"),
  groupBy: yup.string().required("Group by is required"),
  customStartDate: yup.string().when("dateRange", {
    is: "custom",
    then: (schema) => schema.required("Start date is required for custom range"),
    otherwise: (schema) => schema.notRequired()
  }),
  customEndDate: yup.string().when("dateRange", {
    is: "custom", 
    then: (schema) => schema.required("End date is required for custom range"),
    otherwise: (schema) => schema.notRequired()
  })
}).required();

const AddEmailModal = ({ isOpen, onClose, onAdd, onRunAgent, isGA4Connected }) => {
  const [compareEnabled, setCompareEnabled] = useState(false);
  const [compareRange, setCompareRange] = useState("sameLastYear");
  const [customCompareStartDate, setCustomCompareStartDate] = useState("");
  const [customCompareEndDate, setCustomCompareEndDate] = useState("");
  const [selectedQuickRange, setSelectedQuickRange] = useState(30); // Default to 30 days
  const [periodType, setPeriodType] = useState("rolling"); // New state for period type
  const [isCreating, setIsCreating] = useState(false); // Loading state for thread creation
  const [step, setStep] = useState(1); // Wizard step: 1 = time controls, 2 = prompt
  
  const { orgId } = useOrganization();
  const { userInfo } = useUser();

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
    reset,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      content: "",
      agentType: "ga4",
      dateRange: "last28days",
      groupBy: "daily",
      customStartDate: "",
      customEndDate: "",
    }
  });

  const watchedDateRange = watch("dateRange");
  const watchedStartDate = watch("customStartDate");
  const watchedEndDate = watch("customEndDate");

  // Helper to format date as YYYY-MM-DD in local timezone (avoids UTC conversion issues)
  const formatDateLocal = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Initialize default rolling period when component mounts
  useEffect(() => {
    // Set default 30-day rolling period when modal opens
    // IMPORTANT: Always exclude today since GA4 data is partial for the current day
    if (isOpen && periodType === "rolling") {
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      
      const startDate = new Date(yesterday);
      startDate.setDate(yesterday.getDate() - 29); // 30 days total (yesterday - 29 days)
      
      setValue("customStartDate", formatDateLocal(startDate));
      setValue("customEndDate", formatDateLocal(yesterday));
      setValue("dateRange", "last30days");
    }
  }, [isOpen, setValue]);

  // Clear error when modal closes
  useEffect(() => {
    if (!isOpen) {
      setStep(1); // Reset wizard to step 1 when modal closes
    }
  }, [isOpen]);

  // Quick date range helpers
  // IMPORTANT: Always exclude today since GA4 data is partial for the current day
  const setQuickRange = (days) => {
    setPeriodType("rolling");
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    
    const startDate = new Date(yesterday);
    startDate.setDate(yesterday.getDate() - (days - 1)); // e.g., 7 days = yesterday - 6 days
    
    setValue("customStartDate", formatDateLocal(startDate));
    setValue("customEndDate", formatDateLocal(yesterday));
    setValue("dateRange", `last${days}days`);
    setSelectedQuickRange(days);
  };

  // Handle period type change
  const handlePeriodTypeChange = (type) => {
    setPeriodType(type);
    if (type === "rolling") {
      // Reset to default rolling period
      setQuickRange(30);
      setCompareRange("sameLastYear");
    } else {
      // Clear quick range selection for fixed period
      setSelectedQuickRange(null);
      setValue("dateRange", "custom");
    }
  };

  // Reset quick range selection when dates are manually changed
  useEffect(() => {
    if (watchedStartDate && watchedEndDate) {
      const start = new Date(watchedStartDate);
      const end = new Date(watchedEndDate);
      const diffTime = Math.abs(end - start);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1; // +1 for inclusive range
      
      // Check if the current dates match any quick range (end date should be yesterday)
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      const yesterdayStr = formatDateLocal(yesterday);
      
      if (watchedEndDate === yesterdayStr) {
        if (diffDays === 7) setSelectedQuickRange(7);
        else if (diffDays === 30) setSelectedQuickRange(30);
        else if (diffDays === 90) setSelectedQuickRange(90);
        else setSelectedQuickRange(null);
      } else {
        setSelectedQuickRange(null);
      }
    }
  }, [watchedStartDate, watchedEndDate]);

  // Validation helper
  const validateDateRange = (start, end) => {
    const startDate = new Date(start);
    const endDate = new Date(end);
    const diffTime = Math.abs(endDate - startDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays <= 180;
  };

  const buildTimeControlsString = (data) => {
    let timeString = `Date Range: ${data.dateRange === "custom" ? `${data.customStartDate} to ${data.customEndDate}` : data.dateRange}`;
    timeString += ` | Group by: ${data.groupBy}`;
    
    if (compareEnabled) {
      if (compareRange === "custom") {
        timeString += ` | Compare to: ${customCompareStartDate} to ${customCompareEndDate}`;
      } else {
        timeString += ` | Compare to: ${compareRange}`;
      }
    }
    
    return timeString;
  };

  const handleNext = () => {
    // Validate step 1 before proceeding
    if (periodType === "fixed") {
      if (!watchedStartDate || !watchedEndDate) {
        toast.error("Please select both start and end dates", { duration: 3000, position: 'top-right' });
        return;
      }
      if (!validateDateRange(watchedStartDate, watchedEndDate)) {
        toast.error("Date range cannot exceed 180 days", { duration: 3000, position: 'top-right' });
        return;
      }
    }
    if (compareEnabled && compareRange === "custom") {
      if (!customCompareStartDate || !customCompareEndDate) {
        toast.error("Please select comparison dates", { duration: 3000, position: 'top-right' });
        return;
      }
    }
    setStep(2);
  };

  const handleBack = () => setStep(1);

  const onSubmit = async (data) => {
    // Ensure we have date values
    if (!watchedStartDate || !watchedEndDate) {
      alert("Please select a date range");
      return;
    }

    // Validate custom date ranges
    if (periodType === "fixed") {
      if (!validateDateRange(watchedStartDate, watchedEndDate)) {
        alert("Date range cannot exceed 180 days");
        return;
      }
    }

    if (compareEnabled && compareRange === "custom") {
      if (!customCompareStartDate || !customCompareEndDate) {
        alert("Please select both start and end dates for custom comparison range");
        return;
      }
      if (!validateDateRange(customCompareStartDate, customCompareEndDate)) {
        alert("Comparison date range cannot exceed 180 days");
        return;
      }
    }

    // Build standardized payload
    const dateRanges = [];
    
    // Primary date range
    const primaryRange = {
      id: "primary",
      startDate: watchedStartDate,
      endDate: watchedEndDate,
      calculated: periodType === "rolling"
    };
    dateRanges.push(primaryRange);

    // Comparison date range (if enabled)
    if (compareEnabled) {
      let comparisonStartDate, comparisonEndDate;
      
      if (compareRange === "sameLastYear") {
        // Calculate same period last year
        if (!watchedStartDate || !watchedEndDate) {
          alert("Date range is required for comparison calculation");
          return;
        }
        
        const startDate = new Date(watchedStartDate);
        const endDate = new Date(watchedEndDate);
        startDate.setFullYear(startDate.getFullYear() - 1);
        endDate.setFullYear(endDate.getFullYear() - 1);
        
        comparisonStartDate = startDate.toISOString().split('T')[0];
        comparisonEndDate = endDate.toISOString().split('T')[0];
      } else {
        // Custom comparison dates
        comparisonStartDate = customCompareStartDate;
        comparisonEndDate = customCompareEndDate;
      }

      const comparisonRange = {
        id: "comparison",
        startDate: comparisonStartDate,
        endDate: comparisonEndDate,
        calculated: compareRange === "sameLastYear"
      };
      dateRanges.push(comparisonRange);
    }

    // Build config object
    const config = {
      comparison: {
        enabled: compareEnabled,
        type: compareRange,
        autoCalculated: compareRange === "sameLastYear"
      },
      automation: {
        eligible: periodType === "rolling"
      }
    };

    // Add rolling config if rolling period
    if (periodType === "rolling") {
      config.rolling = {
        days: selectedQuickRange
      };
    }

    // Create thread payload for backend
    const threadPayload = {
      endpoint: "/api/langgraph/ga4", // LangGraph endpoint
      prompt: data.content,
      agentType: data.agentType,
      granularity: data.groupBy,
      periodType: periodType,
      dateRanges: dateRanges,
      config: config,
      status: "in_progress",
      input_data: {
        prompt: data.content,
        agentType: data.agentType,
        granularity: data.groupBy,
        periodType: periodType,
        dateRanges: dateRanges,
        config: config
      }
    };

    try {
      setIsCreating(true);
      
      // Create thread on server (server now handles both MongoDB + LangGraph creation)
      const response = await createThread(orgId, threadPayload);
      
      if (!response?.data?.success) {
        throw new Error(response?.data?.message || 'Failed to create thread');
      }

      const serverThread = response.data.data;

      // Pass server-created thread to parent
      onAdd(serverThread);
      
      // Auto-run the agent with server thread
      if (onRunAgent) {
        onRunAgent(serverThread);
      }
      
      // Reset form
      reset();
      setPeriodType("rolling");
      setCompareEnabled(false);
      setCompareRange("sameLastYear");
      setCustomCompareStartDate("");
      setCustomCompareEndDate("");
      setSelectedQuickRange(30);
      setStep(1);
      onClose();
      
    } catch (error) {
      // Extract server error message if available, otherwise use generic message
      const errorMsg = error.response?.data?.message || error.message || 'Failed to create thread';
      toast.error(errorMsg, {
        duration: 5000,
        position: 'top-right'
      });
    } finally {
      setIsCreating(false);
    }
  };

  if (!isOpen) return null;

  // Show GA4 not connected state
  if (isGA4Connected === false) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
        <div className="w-full max-w-md rounded-2xl bg-background shadow-2xl dark:bg-gray-800 flex flex-col overflow-hidden">
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-amber-100 text-amber-600">
                  <Info className="h-4 w-4" />
                </div>
                <h2 className="text-lg font-medium text-foreground">GA4 Not Connected</h2>
              </div>
              <button onClick={onClose} className="rounded-lg p-1.5 text-grey hover:bg-background hover:text-foreground">
                <X className="h-4 w-4" />
              </button>
            </div>
            <p className="text-sm text-grey mb-6 leading-relaxed">
              You need to connect your Google Analytics 4 property before creating analysis threads. Head to your organization settings to set up the connection.
            </p>
            <div className="flex items-center gap-3">
              <Link
                to={`/organization/${orgId}/settings`}
                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-brand-pink hover:bg-brand-pink/90 text-white text-sm font-medium rounded-lg transition-all duration-200"
                onClick={onClose}
              >
                Go to Settings
                <ArrowRight className="h-4 w-4" />
              </Link>
              <button
                onClick={onClose}
                className="px-4 py-2.5 text-sm font-medium text-grey hover:text-foreground rounded-lg border border-border hover:bg-background transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="w-full max-w-4xl max-h-[92vh] rounded-2xl bg-background shadow-2xl dark:bg-gray-800 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex-shrink-0 p-6 pb-4">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-brand-blue-light text-brand-blue-dark">
                <Bot className="h-5 w-5" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-foreground">
                  Start Analysis Thread
                </h2>
                <p className="text-xs text-grey mt-0.5">
                  Step {step} of 2 — {step === 1 ? 'Configure time period' : 'Write your prompt'}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="rounded-lg p-1.5 text-grey hover:bg-background hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          
          {/* Step Indicator */}
          <div className="flex justify-center">
            <div className="flex items-center gap-0">
              {/* Step 1 */}
              <div className="flex flex-col items-center gap-1.5 w-20">
                <div className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-semibold transition-all duration-200 ${
                  step === 1 
                    ? 'bg-brand-blue-dark text-white' 
                    : 'bg-emerald-500 text-white'
                }`}>
                  {step > 1 ? (
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  ) : '1'}
                </div>
                <span className={`text-[11px] font-medium ${
                  step === 1 ? 'text-brand-blue-dark' : 'text-emerald-600'
                }`}>Time Period</span>
              </div>
              
              {/* Connector */}
              <div className={`h-px w-24 -mt-5 transition-colors duration-200 ${step > 1 ? 'bg-emerald-400' : 'bg-gray-200 dark:bg-gray-600'}`}></div>
              
              {/* Step 2 */}
              <div className="flex flex-col items-center gap-1.5 w-20">
                <div className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-semibold transition-all duration-200 ${
                  step === 2 
                    ? 'bg-brand-blue-dark text-white' 
                    : 'bg-gray-200 text-gray-400 dark:bg-gray-600 dark:text-gray-400'
                }`}>2</div>
                <span className={`text-[11px] font-medium ${
                  step === 2 ? 'text-brand-blue-dark' : 'text-grey'
                }`}>Prompt</span>
              </div>
            </div>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-6 pt-2">
          <form onSubmit={handleSubmit(onSubmit)} className="h-full flex flex-col">
            
            {/* ==================== STEP 1: Time Controls ==================== */}
            {step === 1 && (
              <div className="space-y-4 flex-1">
                {/* Time Period */}
                <div className="bg-background/50 rounded-lg p-5 border border-border/50">
                  <p className="text-sm font-medium text-foreground mb-4">When should we analyze?</p>
                  
                  {/* Period Type Selection */}
                  <div className="mb-4">
                    <div className="grid grid-cols-2 gap-1 p-1 bg-background/50 rounded-lg border border-border/30">
                      <button
                        type="button"
                        onClick={() => handlePeriodTypeChange("rolling")}
                        className={`flex items-center justify-center gap-2 px-3 py-2.5 text-xs font-medium rounded-md transition-all duration-200 ${
                          periodType === "rolling"
                            ? 'bg-white text-foreground shadow-sm ring-1 ring-border/50'
                            : 'text-grey hover:text-foreground hover:bg-white/50'
                        }`}
                      >
                        <TrendingUp className="h-3.5 w-3.5" />
                        Rolling
                      </button>
                      <button
                        type="button"
                        onClick={() => handlePeriodTypeChange("fixed")}
                        className={`flex items-center justify-center gap-2 px-3 py-2.5 text-xs font-medium rounded-md transition-all duration-200 ${
                          periodType === "fixed"
                            ? 'bg-white text-foreground shadow-sm ring-1 ring-border/50'
                            : 'text-grey hover:text-foreground hover:bg-white/50'
                        }`}
                      >
                        <Calendar className="h-3.5 w-3.5" />
                        Fixed
                      </button>
                    </div>
                  </div>

                  {/* Rolling Period Content */}
                  {periodType === "rolling" && (
                    <>
                      <div className="text-xs text-grey mb-3">Rolling period can be automated after testing the first run</div>
                      <div className="grid grid-cols-3 gap-2 mb-3">
                        <button 
                          type="button" 
                          onClick={() => setQuickRange(7)} 
                          className={`px-3 py-2.5 text-xs font-medium rounded-lg border transition-colors ${
                            selectedQuickRange === 7
                              ? 'border-brand-pink text-brand-pink bg-brand-pink/5'
                              : 'border-border hover:border-brand-pink/50 text-grey'
                          }`}
                        >
                          Last 7 days
                        </button>
                        <button 
                          type="button" 
                          onClick={() => setQuickRange(30)} 
                          className={`px-3 py-2.5 text-xs font-medium rounded-lg border transition-colors ${
                            selectedQuickRange === 30
                              ? 'border-brand-pink text-brand-pink bg-brand-pink/5'
                              : 'border-border hover:border-brand-pink/50 text-grey'
                          }`}
                        >
                          Last 30 days
                        </button>
                        <button 
                          type="button" 
                          onClick={() => setQuickRange(90)} 
                          className={`px-3 py-2.5 text-xs font-medium rounded-lg border transition-colors ${
                            selectedQuickRange === 90
                              ? 'border-brand-pink text-brand-pink bg-brand-pink/5'
                              : 'border-border hover:border-brand-pink/50 text-grey'
                          }`}
                        >
                          Last 90 days
                        </button>
                      </div>
                      
                      {/* Show calculated date range */}
                      {watchedStartDate && watchedEndDate && (
                        <div className="px-3 py-2 bg-brand-pink/5 rounded-lg border border-brand-pink/20 text-xs text-foreground mb-3">
                          <span className="font-medium">{watchedStartDate}</span>
                          <span className="text-grey mx-2">→</span>
                          <span className="font-medium">{watchedEndDate}</span>
                        </div>
                      )}
                      
                      <div className="text-xs text-grey flex items-center gap-1.5">
                        <Info className="h-3.5 w-3.5 text-brand-pink flex-shrink-0" />
                        <span>Data excludes today </span>
                      </div>
                    </>
                  )}

                  {/* Fixed Period Content */}
                  {periodType === "fixed" && (
                    <>
                      <div className="text-xs text-grey mb-3">Select a specific historical date range</div>
                      <div className="grid grid-cols-2 gap-3 mb-3">
                        <div>
                          <label className="text-xs text-grey mb-1 block">Start date</label>
                          <input
                            type="date"
                            {...register("customStartDate")}
                            className="w-full rounded-lg border border-border px-3 py-2.5 text-xs focus:border-brand-pink focus:outline-none bg-background text-foreground"
                          />
                        </div>
                        <div>
                          <label className="text-xs text-grey mb-1 block">End date</label>
                          <input
                            type="date"
                            {...register("customEndDate")}
                            className="w-full rounded-lg border border-border px-3 py-2.5 text-xs focus:border-brand-pink focus:outline-none bg-background text-foreground"
                          />
                        </div>
                      </div>
                    </>
                  )}
                  
                  {(errors.customStartDate || errors.customEndDate) && (
                    <p className="mt-2 text-sm text-red-500">{errors.customStartDate?.message || errors.customEndDate?.message}</p>
                  )}
                </div>

                {/* Granularity */}
                <div className="bg-background/50 rounded-lg p-4 border border-border/50">
                  <p className="text-xs font-medium text-grey mb-3">Granularity</p>
                  <select
                    {...register("groupBy")}
                    className="w-full rounded-lg border border-border px-3 py-2.5 text-xs focus:border-brand-pink focus:outline-none bg-background text-foreground"
                  >
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                  </select>
                  {errors.groupBy && (
                    <p className="mt-2 text-xs text-red-500">{errors.groupBy.message}</p>
                  )}
                </div>

                {/* Compare Toggle */}
                <div className="bg-background/50 rounded-lg p-4 border border-border/50">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={compareEnabled}
                      onChange={(e) => setCompareEnabled(e.target.checked)}
                      className="rounded border-border text-brand-pink focus:ring-1 focus:ring-brand-pink/20"
                    />
                    <span className="text-xs font-medium text-foreground">Compare periods</span>
                  </label>
                  
                  {compareEnabled && (
                    <div className="mt-3 space-y-2">
                      {periodType === "rolling" && (
                        <div className="px-3 py-2 bg-background/50 rounded border border-border text-xs text-grey">
                          Same period last year (automatic)
                        </div>
                      )}
                      
                      {periodType === "fixed" && (
                        <>
                          <select
                            value={compareRange}
                            onChange={(e) => setCompareRange(e.target.value)}
                            className="w-full rounded border border-border px-3 py-2 text-xs focus:border-brand-pink focus:outline-none bg-background text-foreground"
                          >
                            <option value="sameLastYear">Same period last year</option>
                            <option value="custom">Custom period</option>
                          </select>
                          
                          {compareRange === "custom" && (
                            <div className="grid grid-cols-2 gap-2">
                              <input
                                type="date"
                                value={customCompareStartDate}
                                onChange={(e) => setCustomCompareStartDate(e.target.value)}
                                className="w-full rounded border border-border px-3 py-2 text-xs bg-background text-foreground focus:border-brand-pink focus:outline-none"
                              />
                              <input
                                type="date"
                                value={customCompareEndDate}
                                onChange={(e) => setCustomCompareEndDate(e.target.value)}
                                className="w-full rounded border border-border px-3 py-2 text-xs bg-background text-foreground focus:border-brand-pink focus:outline-none"
                              />
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ==================== STEP 2: Prompt + Agent Type ==================== */}
            {step === 2 && (
              <div className="space-y-4 flex-1">
                {/* Time Period Summary - clickable to go back */}
                <div className="bg-brand-pink/5 rounded-lg px-4 py-3 border border-brand-pink/20 flex items-center justify-between">
                  <div className="flex items-center gap-3 text-xs">
                    <Clock className="h-4 w-4 text-brand-pink flex-shrink-0" />
                    <div className="text-foreground">
                      <span className="font-medium">{watchedStartDate}</span>
                      <span className="text-grey mx-1.5">→</span>
                      <span className="font-medium">{watchedEndDate}</span>
                      <span className="text-grey ml-2">·</span>
                      <span className="text-grey ml-2 capitalize">{watch("groupBy")}</span>
                      {compareEnabled && (
                        <>
                          <span className="text-grey ml-2">·</span>
                          <span className="text-grey ml-2">vs last year</span>
                        </>
                      )}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={handleBack}
                    className="text-xs text-brand-pink hover:underline font-medium"
                  >
                    Edit
                  </button>
                </div>

                {/* Agent Type */}
                <div className="bg-brand-blue-light/10 rounded-lg p-4 border border-brand-blue-light/20">
                  <p className="text-xs font-medium text-grey mb-3">Agent Type</p>
                  <div className="space-y-3">
                    <label className="flex items-start gap-3 cursor-pointer group">
                      <input
                        type="radio"
                        {...register("agentType")}
                        value="ga4"
                        className="mt-0.5 text-brand-pink focus:ring-1 focus:ring-brand-pink/20"
                      />
                      <div className="flex-1">
                        <div className="text-sm font-medium text-foreground group-hover:text-brand-pink transition-colors">
                          Google Analytics 4
                        </div>
                        <div className="text-xs text-grey mt-0.5">
                          Analyze website traffic, user behavior, and conversions
                        </div>
                      </div>
                    </label>
                    
                    <label className="flex items-start gap-3 cursor-not-allowed opacity-50">
                      <input
                        type="radio"
                        {...register("agentType")}
                        value="gsc"
                        disabled
                        className="mt-0.5 text-brand-pink focus:ring-1 focus:ring-brand-pink/20"
                      />
                      <div className="flex-1">
                        <div className="text-sm font-medium text-foreground flex items-center gap-2">
                          Google Search Console
                          <span className="text-xs bg-grey/20 text-grey px-2 py-0.5 rounded">Coming Soon</span>
                        </div>
                        <div className="text-xs text-grey mt-0.5">
                          Analyze search performance, keywords, and indexing
                        </div>
                      </div>
                    </label>
                  </div>
                  {errors.agentType && (
                    <p className="mt-2 text-sm text-red-500">{errors.agentType.message}</p>
                  )}
                </div>

                {/* Prompt */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-medium text-foreground">What would you like to analyze?</p>
                    <span className="text-xs text-grey">
                      Need inspiration? Browse the <Link to="/prompt-library" target="_blank" className="font-medium text-brand-pink hover:underline">Prompt Library</Link>
                    </span>
                  </div>
                  <textarea
                    {...register("content")}
                    placeholder="e.g., What are my top traffic sources and how are they trending?"
                    rows={5}
                    className="w-full rounded-lg border border-border px-3 py-2.5 text-sm focus:border-brand-pink focus:outline-none bg-background text-foreground resize-none transition-colors"
                    autoFocus
                  />
                  {errors.content && (
                    <p className="mt-1 text-sm text-red-500">{errors.content.message}</p>
                  )}
                </div>
              </div>
            )}

            {/* ==================== Footer Navigation ==================== */}
            <div className="flex justify-between items-center gap-3 pt-5 mt-4 border-t border-border">
              {step === 1 ? (
                <>
                  <button
                    type="button"
                    onClick={onClose}
                    className="rounded-lg border border-border px-4 py-2.5 text-sm font-medium text-grey hover:bg-background hover:text-foreground transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleNext}
                    className="rounded-lg bg-brand-pink px-5 py-2.5 text-sm font-medium text-white hover:bg-brand-pink/90 transition-colors flex items-center gap-2"
                  >
                    Continue
                    <ArrowRight className="h-4 w-4" />
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={handleBack}
                    className="rounded-lg border border-border px-4 py-2.5 text-sm font-medium text-grey hover:bg-background hover:text-foreground transition-colors flex items-center gap-2"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    Back
                  </button>
                  <button
                    type="submit"
                    disabled={isCreating}
                    className="rounded-lg bg-brand-pink px-5 py-2.5 text-sm font-medium text-white hover:bg-brand-pink/90 focus:outline-none focus:ring-2 focus:ring-brand-pink/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {isCreating ? (
                      <>
                        <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Creating...
                      </>
                    ) : (
                      'Create Thread'
                    )}
                  </button>
                </>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddEmailModal;